import { call, put, select, takeLatest } from 'redux-saga/effects'
import { SagaIterator } from 'redux-saga'
import { createAction } from '@reduxjs/toolkit'
import {
  fetchSessionToken,
  refreshSessionToken,
  logoutSession,
  withCsrf,
  clearCsrfToken,
  normalizeSessionToken,
  expiresInToMs,
  initiateLogin,
  MsAuthApiError,
} from '../apis/msBackendAuth.api'
import {
  setSessionCredentials,
  setMsBackendAuthError,
  setMsBackendAuthChecking,
  logoutAuthProfile,
  getUserInfo,
  clearSession,
} from '../slices'
import {
  LoginTypeEnum,
  SessionTokenResponse,
  SessionState,
  UserTypeEnum,
} from '../types'

// ─── Action types ────────────────────────────────────────────────────────────

/**
 * Dispatched by the guard on every mount. Carries the portal's user type:
 * this saga is shared by agent-desk and admin-desk, and the profile fetch
 * needs to ask for the right one — a hardcoded value would load agent
 * permissions into the admin portal.
 */
export const msBackendAuthInit = createAction<{ userType: UserTypeEnum }>(
  'msBackendAuth/init'
)

/** Dispatched to perform a backend logout. */
export const msBackendLogout = createAction('msBackendAuth/logout')

/**
 * sessionStorage key for the redirect-loop guard. Exported because the route
 * guards and the logout paths in both portals set/clear the same flag — four
 * copies of a string literal is one typo away from a silent redirect loop.
 */
export const MS_BACKEND_LOGIN_ATTEMPT_KEY = 'ms_backend_login_attempt'
const LOGIN_ATTEMPT_KEY = MS_BACKEND_LOGIN_ATTEMPT_KEY

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Commit a successful session-token response: credentials, user info, done. */
function* commitTokenSuccess(
  tokenRes: SessionTokenResponse,
  userType: UserTypeEnum
): SagaIterator {
  const { accessToken, expiresIn } = normalizeSessionToken(tokenRes)
  // Boundary validation: a 2xx with no usable access token is a malformed
  // contract, not a valid session. Throw so the boot catch handles it
  // (login / error) instead of marking the app authenticated with an empty
  // bearer that would immediately 401 every business call.
  if (!accessToken) {
    throw new Error('session-token response contained no access token')
  }
  yield put(
    setSessionCredentials({
      isAuthenticated: true,
      token: accessToken,
      // Refresh is now cookie-based (HttpOnly) — the FE holds no refresh token.
      refreshToken: null,
      tokenExpiresAtMs: expiresInToMs(expiresIn),
      sessionLoginAtMs: Date.now(),
      maxSessionExtensionCount: 0,
    })
  )
  yield put(
    getUserInfo({
      token: accessToken,
      source: LoginTypeEnum.LOGIN,
      user_type: userType,
    })
  )
  yield put(setMsBackendAuthChecking(false))
}

// ─── Boot flow (hardened) ────────────────────────────────────────────────────
//
// A single cookie-based bootstrap replaces the old pickup-cookie/relay branches.
// The access token is memory-only, so on any load where it's absent we exchange
// the HttpOnly session cookie (+ CSRF) for a fresh one. This one path covers both:
//   • returning from the Microsoft OAuth redirect (cookie freshly set by /callback)
//   • reloading an already-authenticated tab (token gone from memory, cookie lives)
// Only a fresh visitor / expired session (no cookie → /csrf or /session-token
// fails) falls through to the interactive Microsoft redirect.

/** Exported for unit tests — dispatched only via the msBackendAuthInit action. */
export function* msBackendAuthInitSaga(
  action: ReturnType<typeof msBackendAuthInit>
): SagaIterator {
  const { userType } = action.payload
  yield put(setMsBackendAuthChecking(true))

  // Token already in memory (a re-dispatch within the same page life) — proceed.
  // The Axios interceptor handles expiry reactively (401 → refresh → retry).
  const existingToken: string | null = yield select(
    (state: { session: SessionState }) => state.session.token
  )
  if (existingToken) {
    yield put(setMsBackendAuthChecking(false))
    return
  }

  const previouslyAttempted = sessionStorage.getItem(LOGIN_ATTEMPT_KEY) === '1'

  // Captured before the async call so we can detect a logout (or any other newer
  // auth cycle) that completes while the request is in flight.
  const epochAtStart: number = yield select(
    (state: { session: SessionState }) => state.session.authEpoch ?? 0
  )

  try {
    const tokenRes: SessionTokenResponse = yield call(
      withCsrf,
      fetchSessionToken
    )
    sessionStorage.removeItem(LOGIN_ATTEMPT_KEY)

    const epochNow: number = yield select(
      (state: { session: SessionState }) => state.session.authEpoch ?? 0
    )
    if (epochNow !== epochAtStart) {
      // A newer auth cycle (e.g. a logout) already superseded this request.
      return
    }
    yield call(commitTokenSuccess, tokenRes, userType)
  } catch (err) {
    const epochNow: number = yield select(
      (state: { session: SessionState }) => state.session.authEpoch ?? 0
    )
    if (epochNow !== epochAtStart) {
      return
    }

    if (previouslyAttempted) {
      // We already came back from the Microsoft redirect and STILL couldn't get a
      // token — stop here rather than bouncing to Microsoft again in a loop.
      sessionStorage.removeItem(LOGIN_ATTEMPT_KEY)
      console.warn(
        '[msBackendAuth] boot: session-token failed after OAuth return — showing error',
        err instanceof MsAuthApiError ? err.status : err
      )
      yield put(
        setMsBackendAuthError(
          'Sign-in could not complete. Please reload or contact support.'
        )
      )
      yield put(setMsBackendAuthChecking(false))
      return
    }

    // Fresh visitor / expired session (no valid cookie) → interactive login.
    // Pass the current deep link as a RELATIVE returnPath (path + query + hash);
    // the backend appends it to its allowlisted UI origin to bring the browser
    // back here after the Microsoft round trip. It rejects an absolute URL
    // (AUTH_REQUEST_INVALID), so NOT window.location.href. (The Referer can't be
    // relied on either — truncated to the origin on cross-origin navigations.)
    console.info(
      '[msBackendAuth] boot: no active session — redirecting to Microsoft login'
    )
    sessionStorage.setItem(LOGIN_ATTEMPT_KEY, '1')
    const returnPath =
      window.location.pathname + window.location.search + window.location.hash
    initiateLogin(returnPath)
  }
}

// ─── Logout ─────────────────────────────────────────────────────────────────

function* msBackendLogoutSaga(): SagaIterator {
  // Revoke the backend session (cookie + CSRF). Best-effort: proceed with local
  // cleanup even if the call fails, so the user is never stuck "logged in" locally.
  try {
    yield call(withCsrf, logoutSession)
  } catch {
    /* proceed with local cleanup even if the backend call fails */
  }
  yield call(clearCsrfToken)
  sessionStorage.removeItem(LOGIN_ATTEMPT_KEY)
  yield put(logoutAuthProfile())
  yield put(clearSession())
  window.location.href = `${window.location.origin}/logout`
}

// ─── Token refresh — called directly from the axios interceptor ──────────────
// Plain async function (not a saga effect) so the interceptor can await it and
// retry the original request with the fresh token. Proof is the HttpOnly session
// cookie + CSRF + Origin — no Bearer, no argument.

export async function performMsBackendTokenRefresh(): Promise<SessionTokenResponse | null> {
  try {
    return await withCsrf(refreshSessionToken)
  } catch (err) {
    // A confirmed 401 means the session/refresh is gone — return null so the
    // caller cleans up and re-initiates login rather than hard-erroring.
    if (err instanceof MsAuthApiError && err.status === 401) {
      return null
    }
    throw err
  }
}

// ─── Watchers ─────────────────────────────────────────────────────────────────

export function* watchMsBackendAuthInit() {
  yield takeLatest(msBackendAuthInit, msBackendAuthInitSaga)
}

export function* watchMsBackendLogout() {
  yield takeLatest(msBackendLogout, msBackendLogoutSaga)
}
